import { useState } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { toast } from "sonner";
import { SrIcon } from "@/components/ui/SrIcon";
import { EditAbsenceDialog } from "./EditAbsenceDialog";
import { useAbsences, useDeleteAbsence, type Absence } from "@/hooks/use-absences";
import { formatHours } from "@/lib/format";
import {
  ABSENCE_TYPE_DOTS,
  ABSENCE_TYPE_LABELS,
  absenceDisplayHours,
  filterAbsencesInRange,
} from "@/lib/absence-display";
import { formatAbsenceTimeRange } from "@/lib/absence-time";

interface AbsenceRangeSummaryProps {
  userId: string | null;
  rangeStart: Date;
  rangeEnd: Date;
}

/**
 * A4-smoke-fiks (2026-07-18, VIB-1221): registrert fravær (syk/ferie/
 * avspasering) var synlig i Fravær-fanen/Avslutt måned, men IKKE i
 * timesoversikten på Mine oppgaver ("Timer"-kortet, uke-/månedsvisning) —
 * den kortet spurte kun `time_entries`, aldri `absences`. Denne komponenten
 * viser fravær som overlapper det synlige uke-/månedsvinduet, samme
 * datakilde (`useAbsences`) og visning som `CloseMonthAbsence` (konsistent
 * på tvers av løpende registrering og månedslukking). Skjules helt uten
 * fravær i perioden — ingen ekstra støy i det vanlige tilfellet.
 *
 * Ingen REGISTRERINGS-UI her (det dekkes av «Meld fravær» på
 * `TimeRegistrationPage` og av skjemaet i `CloseMonthAbsence`), men sletting
 * hører hjemme her: dette er stedet den ansatte faktisk ser fraværet sitt i
 * det daglige. Uten det måtte man innom «Lukk måned» for å rette en
 * feilregistrering, som er en omvei ingen finner (smoke-test 10.08).
 */
export default function AbsenceRangeSummary({ userId, rangeStart, rangeEnd }: AbsenceRangeSummaryProps) {
  const { data: allAbsences = [] } = useAbsences(userId ?? undefined);
  const { mutate: deleteAbsence, isPending: isDeleting } = useDeleteAbsence();
  const [editing, setEditing] = useState<Absence | null>(null);
  const rangeStartStr = format(rangeStart, "yyyy-MM-dd");
  const rangeEndStr = format(rangeEnd, "yyyy-MM-dd");
  const absences = filterAbsencesInRange(allAbsences, rangeStartStr, rangeEndStr);

  if (absences.length === 0) return null;

  const handleDelete = (id: string) => {
    if (!userId) return;
    deleteAbsence({ id, user_id: userId }, { onSuccess: () => toast.success("Fravær slettet") });
  };

  return (
    <div className="px-4 py-2 border-b border-border/50 bg-muted/20">
      <p className="text-xs text-muted-foreground font-medium mb-1.5">Fravær i perioden</p>
      <div className="space-y-1.5">
        {absences.map((absence) => (
          <div key={absence.id} className="group flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${ABSENCE_TYPE_DOTS[absence.type]}`}
                aria-hidden
              />
              <span className="font-medium">{ABSENCE_TYPE_LABELS[absence.type]}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-muted-foreground tabular-nums">
                {formatHours(absenceDisplayHours(absence))}
              </span>
              <span className="text-muted-foreground whitespace-nowrap">
                {format(parseISO(absence.start_date), "dd.MM", { locale: nb })}
                {formatAbsenceTimeRange(absence.start_time, absence.end_time) ? (
                  <> kl. {formatAbsenceTimeRange(absence.start_time, absence.end_time)}</>
                ) : absence.start_date !== absence.end_date ? (
                  <>{" – "}{format(parseISO(absence.end_date), "dd.MM", { locale: nb })}</>
                ) : null}
              </span>
              {/* Full trykkflate på touch; på store skjermer vises knappene
                  først ved hover, så listen forblir rolig å lese. */}
              <button
                type="button"
                onClick={() => setEditing(absence)}
                className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center rounded hover:bg-muted xl:opacity-0 xl:group-hover:opacity-100 xl:focus-visible:opacity-100 xl:group-focus-within:opacity-100 transition-opacity"
                aria-label={`Endre ${ABSENCE_TYPE_LABELS[absence.type].toLowerCase()} ${format(parseISO(absence.start_date), "dd.MM", { locale: nb })}`}
              >
                <SrIcon name="pencil" className="w-4 h-4 xl:w-3 xl:h-3 text-muted-foreground" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(absence.id)}
                disabled={isDeleting}
                className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center rounded hover:bg-destructive/10 disabled:opacity-40 xl:opacity-0 xl:group-hover:opacity-100 xl:focus-visible:opacity-100 xl:group-focus-within:opacity-100 transition-opacity"
                aria-label={`Slett ${ABSENCE_TYPE_LABELS[absence.type].toLowerCase()} ${format(parseISO(absence.start_date), "dd.MM", { locale: nb })}`}
              >
                <SrIcon name="x" className="w-4 h-4 xl:w-3 xl:h-3 text-destructive" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <EditAbsenceDialog absence={editing} onOpenChange={(o) => !o && setEditing(null)} />
    </div>
  );
}
